import React, { Component } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ThemeContext } from './components/Core/ThemeContext';
import SEO from './components/SEO';

const contactPageStyles = {
    page: {
        minHeight: '100vh',
        padding: 'clamp(16px, 3vw, 28px)',
        background: 'linear-gradient(180deg, #f8f8f4 0%, #edf4f3 42%, #f9fbfb 100%)',
    },
    frame: {
        width: 'min(100%, 940px)',
        margin: '0 auto',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
    },
    heroShell: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 14px 32px rgba(86, 96, 110, 0.1)',
    },
    heroStrip: {
        width: 18,
        minWidth: 18,
        background: 'var(--color-grad-1)',
    },
    heroBody: {
        flex: 1,
        padding: 'clamp(18px, 3vw, 26px)',
    },
    eyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    heroTitle: {
        margin: '6px 0 0 0',
        fontSize: 'clamp(1.9em, 4vw, 2.45em)',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
        lineHeight: 1.1,
    },
    heroSummary: {
        margin: '12px 0 0 0',
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '1em',
        lineHeight: 1.7,
        maxWidth: 720,
    },
    shell: {
        display: 'flex',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(86, 96, 110, 0.08)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    strip: {
        width: 12,
        minWidth: 12,
        background: 'var(--color-grad-1)',
    },
    content: {
        flex: 1,
        minWidth: 0,
        background: 'linear-gradient(180deg, #fffefb 0%, #fcfdfd 100%)',
    },
    summaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px clamp(16px, 2.6vw, 22px)',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    summaryText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    summaryTitle: {
        margin: 0,
        color: 'rgba(0, 0, 0, 0.8)',
        fontSize: '1.05em',
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
    divider: {
        height: 1,
        background: 'rgba(188, 198, 208, 0.32)',
        margin: '0 clamp(16px, 2.6vw, 22px)',
    },
    body: {
        padding: '16px clamp(16px, 2.6vw, 22px) 22px',
        color: 'rgba(0, 0, 0, 0.76)',
    },
    collapsedBody: {
        maxHeight: 0,
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    },
    paragraph: {
        margin: '0 0 14px 0',
        color: 'rgba(0, 0, 0, 0.76)',
        fontSize: '0.98em',
        lineHeight: 1.76,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
    },
    label: {
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '0.9em',
        fontWeight: 700,
    },
    input: {
        width: '100%',
        minHeight: 44,
        padding: '9px 11px',
        border: '1px solid rgba(126, 145, 151, 0.55)',
        borderRadius: 6,
        background: '#fff',
        color: 'rgba(0, 0, 0, 0.78)',
        font: 'inherit',
        fontSize: '1em',
        boxSizing: 'border-box',
    },
    textarea: {
        minHeight: 160,
        resize: 'vertical',
    },
    captcha: {
        overflowX: 'auto',
        maxWidth: '100%',
    },
    button: {
        width: '100%',
        minHeight: 46,
        padding: '10px 18px',
        border: 0,
        borderRadius: 6,
        background: 'var(--color-grad-1)',
        color: '#fff',
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 700,
    },
};

function ContactHero() {
    return (
        <div style={contactPageStyles.heroShell}>
            <div style={contactPageStyles.heroStrip} />
            <div style={contactPageStyles.heroBody}>
                <p style={contactPageStyles.eyebrow}>Get in touch</p>
                <h1 style={contactPageStyles.heroTitle}>Contact</h1>
                <p style={contactPageStyles.heroSummary}>
                    Questions, feedback, and support for the ai-BIBLE project.
                    The form is designed to remain comfortable to use on desktop and mobile.
                </p>
            </div>
        </div>
    );
}

function ContactSection({ title, eyebrow, children, startsOpen = true }) {
    const [open, setOpen] = React.useState(startsOpen);

    return (
        <section style={contactPageStyles.shell}>
            <div style={contactPageStyles.strip} aria-hidden="true" />
            <div style={contactPageStyles.content}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={contactPageStyles.summaryButton}
                >
                    <span style={contactPageStyles.summaryText}>
                        <span style={contactPageStyles.eyebrow}>{eyebrow}</span>
                        <h2 style={contactPageStyles.summaryTitle}>{title}</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={contactPageStyles.divider} aria-hidden="true" />
                <div
                    aria-hidden={!open}
                    style={{
                        ...contactPageStyles.body,
                        ...(open ? null : contactPageStyles.collapsedBody),
                    }}
                >
                    {children}
                </div>
            </div>
        </section>
    );
}

class ContactUs extends Component {
    static contextType = ThemeContext;

    constructor() {
        super();

        this.state = {
            email: '',
            body: '',
            subject: '',
            app: 'ai-BIBLE'
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidMount() {
        this.context.loading(false);
    }

    componentWillUnmount() {
        console.log('Will Unmount');
    }

    onSubmit(e, d) {
        e.preventDefault();
        console.log({'state': this.state})
        console.log(window.API_BASE);

        const endpoint = window.API_BASE + "/v1/chat/email";
        const payload = {...this.state}
        console.log({payload});

        fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((res) => {
                console.log('res',res);

                if (res.status > 200)
                {
                    throw 'please check email address';
                }

                return res.text()
            })
            .then((resp) => {
                if (!resp.startsWith('Email'))
                    throw 'Invalid Response'

                console.log(`response time: ${new Date().getSeconds()}`);
                console.log('response', resp);


                this.setState({ email: '', subject: '', body: '' });

                alert('Email sent!');

            }).catch((e)=>{
                console.log({e})
                console.log(e.toString())
                alert('Error: ' + e.toString())
            });

            console.log('final');

    }

    render() {
        const title = 'Contact ai-BIBLE | Questions, Feedback and Support';
        const description = 'Contact ai-BIBLE for support, questions, feedback, or partnership enquiries.';
        document.title = title;
        return (
            <div style={contactPageStyles.page}>
                <SEO canonical="https://www.ai-bible.com/contact" title={title} description={description} />
                <div style={contactPageStyles.frame}>
                    <div style={contactPageStyles.stack}>
                        <ContactHero />

                        <ContactSection title="About ai-BIBLE" eyebrow="The project">
                            <p style={contactPageStyles.paragraph}>
                                ai-BIBLE is intended to be a Bible that both utilises artificial intelligence and facilitates application and interpretation. It currently uses natural language processing and machine learning. The project aims to provide free materials that are free or public domain and is currently unfunded.
                            </p>
                            <p style={{ ...contactPageStyles.paragraph, marginBottom: 0 }}>
                                If you have a question, feedback, or an idea to share, please use the contact form below.
                            </p>
                        </ContactSection>

                        <ContactSection title="Send a message" eyebrow="Contact form">
                            <form id="ajax-contact" style={contactPageStyles.form} onSubmit={this.onSubmit.bind(this)}>
                                <div style={contactPageStyles.field}>
                                    <label htmlFor="name" style={contactPageStyles.label}>Name</label>
                                    <input type="text" style={contactPageStyles.input} id="name" name="name" required value={this.state.subject} onChange={(e) => this.setState({ subject: e.target.value })} />
                                </div>

                                <div style={contactPageStyles.field}>
                                    <label htmlFor="email" style={contactPageStyles.label}>Email</label>
                                    <input type="email" id="email" name="email" style={contactPageStyles.input} required value={this.state.email} onChange={(e) => this.setState({ email: e.target.value })} />
                                </div>

                                <div style={contactPageStyles.field}>
                                    <label htmlFor="message" style={contactPageStyles.label}>Message</label>
                                    <textarea id="message" name="message" style={{ ...contactPageStyles.input, ...contactPageStyles.textarea }} required value={this.state.body} onChange={(e) => this.setState({ body: e.target.value })} />
                                </div>

                                <div style={contactPageStyles.captcha}>
                                    <ReCAPTCHA sitekey="6LfrH9MUAAAAAFe13mga-k3s6sF7tzi6krOreESP" />
                                </div>

                                <button type="submit" style={contactPageStyles.button}>Send message</button>
                            </form>
                        </ContactSection>
                    </div>
                </div>
            </div>
        );
    }
}

export default ContactUs;
